import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import FormMultiSelect from '../_shared/forms/FormMultiSelect';

const mapProviderToOption = (provider) => ({ value: provider.id, label: provider.name });

const formatAddress = (job) =>
    [job.address_line_1, job.address_line_2, job.city, job.postcode].filter(Boolean).join(', ');

const AcceptanceBadge = ({ job }) =>
    job.accepted ? (
        <span className="badge badge-success">Accepted</span>
    ) : (
        <span className="badge badge-warning">Awaiting acceptance</span>
    );

class JobsOverview extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedDate: props.selectedDate,
            providers: props.providers,
            loading: false,
            openIds: new Set(props.providers.map((p) => p.id)),
            selectedProviders: props.providers.map(mapProviderToOption),
        };
    }

    handleProviderFilterChange = (value) => {
        this.setState({ selectedProviders: value || [] });
    };

    loadData = (date) => {
        this.setState({ loading: true });

        axios
            .get('/providers/jobs-overview/data', { params: { date } })
            .then((res) => {
                this.setState({
                    selectedDate: date,
                    providers: res.data.providers,
                    loading: false,
                });
            })
            .catch(() => {
                this.setState({ loading: false });
                toastr.error('Failed to load jobs for that date');
            });
    };

    handleDateChange = (e) => {
        const date = e.target.value;
        if (date) {
            this.loadData(date);
        }
    };

    toggleProvider = (providerId) => {
        this.setState((prevState) => {
            const openIds = new Set(prevState.openIds);
            if (openIds.has(providerId)) {
                openIds.delete(providerId);
            } else {
                openIds.add(providerId);
            }
            return { openIds };
        });
    };

    moveJobToProvider = (jobId, fromProviderId, toProviderId) => {
        if (toProviderId === fromProviderId) {
            return;
        }

        const { providers, selectedDate } = this.state;

        const fromIndex = providers.findIndex((p) => p.id === fromProviderId);
        const toIndex = providers.findIndex((p) => p.id === toProviderId);
        if (fromIndex === -1 || toIndex === -1) {
            return;
        }

        const job = providers[fromIndex].jobs.find((j) => j.id === jobId);
        if (!job) {
            return;
        }

        const newProviders = providers.map((p) => ({ ...p, jobs: p.jobs.slice() }));
        newProviders[fromIndex].jobs = newProviders[fromIndex].jobs.filter((j) => j.id !== jobId);
        newProviders[toIndex].jobs.push(job);

        this.setState({ providers: newProviders });

        axios
            .patch(`/jobs/${jobId}/reallocate`, {
                provider: toProviderId,
                execution_date: selectedDate,
            })
            .then(() => {
                toastr.success('Job moved');
            })
            .catch(() => {
                toastr.error('There was a problem moving the job');
            });
    };

    renderProvider(provider) {
        const { providers, openIds } = this.state;
        const isOpen = openIds.has(provider.id);

        return (
            <div className="order-jobs-holder" key={provider.id}>
                <div className="accordion">
                    <div className="accordion-header" onClick={() => this.toggleProvider(provider.id)}>
                        <h5>
                            {provider.name}
                            <span style={{ float: 'right', fontSize: '30px', cursor: 'pointer' }}>
                                {isOpen ? '-' : '+'}
                            </span>
                        </h5>
                    </div>

                    {isOpen && (
                        <div className="accordion-body">
                            <div className="table-container">
                                <div className="flex-table header transform-text">
                                    <div className="flex-row">Job Number</div>
                                    <div className="flex-row">Type of Job</div>
                                    <div className="flex-row">Customer Name</div>
                                    <div className="flex-row">Site Address</div>
                                    <div className="flex-row">Execution Date</div>
                                    <div className="flex-row">Job Status</div>
                                    <div className="flex-row">Acceptance</div>
                                    <div className="flex-row">Reallocate</div>
                                </div>

                                {provider.jobs.length > 0 ? (
                                    provider.jobs.map((job) => (
                                        <div className="flex-table transform-text" key={job.id}>
                                            <div className="flex-row">{job.id}</div>
                                            <div className="flex-row">{job.job_type?.name}</div>
                                            <div className="flex-row">{job.customer?.name}</div>
                                            <div className="flex-row" style={{ fontSize: '10px' }}>
                                                {formatAddress(job)}
                                            </div>
                                            <div className="flex-row">{job.execution_date}</div>
                                            <div className="flex-row">{job.status}</div>
                                            <div className="flex-row">
                                                <AcceptanceBadge job={job} />
                                            </div>
                                            <div className="flex-row">
                                                <div className="dropdown">
                                                    <button
                                                        className="btn btn-secondary dropdown-toggle"
                                                        type="button"
                                                        data-toggle="dropdown"
                                                    >
                                                        Reallocate
                                                    </button>
                                                    <ul
                                                        className="dropdown-menu"
                                                        style={{ maxHeight: '200px', overflowY: 'auto' }}
                                                    >
                                                        {providers
                                                            .filter((p) => p.id !== provider.id)
                                                            .map((p) => (
                                                                <li key={p.id}>
                                                                    <button
                                                                        className="dropdown-item"
                                                                        onClick={() =>
                                                                            this.moveJobToProvider(
                                                                                job.id,
                                                                                provider.id,
                                                                                p.id
                                                                            )
                                                                        }
                                                                    >
                                                                        {p.name}
                                                                    </button>
                                                                </li>
                                                            ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-center mt-2">No jobs allocated</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { providers, selectedDate, loading, selectedProviders } = this.state;

        const visibleProviders = providers.filter((provider) =>
            selectedProviders.some((selected) => selected.value === provider.id)
        );

        return (
            <div>
                <h4 className="mt-3">SP jobs overview</h4>

                <div className="row">
                    <div className="col-md-8">
                        <FormMultiSelect
                            name="service_providers"
                            label="Service Providers"
                            options={providers.map(mapProviderToOption)}
                            handleMultiSelectChange={this.handleProviderFilterChange}
                            prefixes={selectedProviders}
                        />
                    </div>
                    <div className="col-md-4">
                        <label>Date</label>
                        <input
                            type="date"
                            className="form-control"
                            value={selectedDate}
                            onChange={this.handleDateChange}
                            disabled={loading}
                        />
                    </div>
                </div>

                <div className="row">
                    {visibleProviders.map((provider) => (
                        <div className="col-xl-12" style={{ width: '100%' }} key={provider.id}>
                            {this.renderProvider(provider)}
                        </div>
                    ))}
                </div>
            </div>
        );
    }
}

export default JobsOverview;

if (document.getElementById('sp-jobs-overview')) {
    const element = document.getElementById('sp-jobs-overview');

    ReactDOM.render(
        <JobsOverview
            providers={JSON.parse(element.dataset.providers)}
            selectedDate={element.dataset.selected_date}
        />,
        element
    );
}
